import React, { useState } from "react";
import { useIntl, getLocale } from 'umi';
import { useWeekDayArr } from "@/pages/dashboard/utils/customHook";
import moment from "moment";
import { getWeekDay, timeZoneTransformForShow, getDynamicLocalCnEnText } from "@/pages/dashboard/utils/utils";
import { metricRender } from "@/pages/dashboard/pages/comparison/_config/hooks";
import { getCountryFlag } from '@/pages/home/_config/countries';
import { getMonetUnit } from "@/pages/dashboard/utils/moneyUnit";
import getMetricColumn from "@/pages/dashboard/utils/getMetricColumn";
import { Bubble } from "tea-component";
import SvgIcon from "@/pages/home/components/SvgIcon";
import { getExplainVal } from "@/pages/dashboard/utils/getExplainVal";


// 获取带有UTC的Accumulate选项
export const getAccumulateOptionsWithUTC = (options, timezoneType, suffix) => {
  const { formatMessage } = useIntl();
  const opts = options.map((opt) => {
    if (opt.value === timezoneType) {
      return { text: opt.text + suffix, value: opt.value };
    }
    return { ...opt };
  });
  opts.unshift({ text: formatMessage({ id: 'common_by_Date' }), value: 'date' });
  return opts;
};

export const useMetrics = () => {
  const { formatMessage } = useIntl();

  const options = [
    {
      text: formatMessage({
        id: 'overview_daily_DAU',
      }),
      value: 'active_users',
      rainbowKey: 'DAU',
      isPercent: false,
      decimals: 0,
      isUserType: true,
      dodDecimals: 1,
    },
    {
      text: formatMessage({
        id: 'overview_monthly_New',
      }),
      value: 'new_users',
      rainbowKey: 'New',
      isPercent: false,
      decimals: 0,
      isUserType: true,
      dodDecimals: 1,
    },
    {
      text: formatMessage({
        id: 'overview_daily_D2',
      }),
      value: 'd2',
      rainbowKey: 'D2',
      isPercent: true,
      decimals: 2,
      isUserType: false,
      dodDecimals: 1,
    },
    {
      text: formatMessage({
        id: 'overview_daily_D3',
      }),
      value: 'd3',
      rainbowKey: 'D3',
      isPercent: true,
      decimals: 2,
      isUserType: false,
      dodDecimals: 1,
    },
    {
      text: formatMessage({
        id: 'overview_daily_D7',
      }),
      value: 'd7',
      rainbowKey: 'D7',
      isPercent: true,
      decimals: 2,
      isUserType: false,
      dodDecimals: 1,
    },
    {
      text: formatMessage({
        id: 'overview_TRU',
      }),
      value: 'total_users',
      rainbowKey: 'TNU',
      isPercent: false,
      decimals: 0,
      isUserType: true,
      dodDecimals: 1,
    },
    {
      text: formatMessage({
        id: 'overview_AOT(min)',
      }),
      value: 'aot',
      rainbowKey: 'Avg.Playtime(min)',
      isPercent: false,
      decimals: 2,
      isUserType: false,
      dodDecimals: 1,
    },
    {
      text: formatMessage({
        id: 'overview_Revenue($)',
      }, { unit: getMonetUnit() }),
      value: 'pay_amount',
      rainbowKey: 'Revenue($)',
      isPercent: false,
      decimals: 2,
      isUserType: false,
      dodDecimals: 1,
    },
    {
      text: formatMessage({
        id: 'overview_ARPPU($)',
      }, { unit: getMonetUnit() }),
      value: 'arppu',
      rainbowKey: 'ARPPU($)',
      isPercent: false,
      decimals: 2,
      isUserType: false,
      dodDecimals: 1,
    },
    {
      text: formatMessage({
        id: 'overview_ARPU($)',
      }, { unit: getMonetUnit() }),
      value: 'arpu',
      rainbowKey: 'ARPU($)',
      isPercent: false,
      decimals: 2,
      isUserType: false,
      dodDecimals: 1,
    },
    {
      text: formatMessage({
        id: 'overview_Payer',
      }),
      value: 'paid_users',
      rainbowKey: 'Payer',
      isPercent: false,
      decimals: 0,
      isUserType: true,
      dodDecimals: 1,
    },
  ];
  return {
    options,
  };
};

// 用于宽表，转换数据格式以便宽表使用
export const getArrObj = (arr) => {
  const resultObj = {};
  for (let i = 0;i < arr?.length;i++) {
    resultObj[arr[i].value] = arr[i];
  }
  return resultObj;
};

export const useFunctionWithIntl = (localText, timezoneType, gameCode, selectorList, isAccumulated = false) => {

  const weekDayArr = useWeekDayArr();
  const { formatMessage } = useIntl();

  const DATE_COLUMN = {
    key: 'date',
    header: localText.date,
    width: isAccumulated ? 220 : 170,
    fixed: "left",
    align: "left",
    render(record: any) {
      /** Sum提示需要用到useState，
      *所以拿出来单独处理，
      *不然render里拿不到最新的的state
      */
      return <DateCell record={record} formatMessage={formatMessage} weekDayArr={weekDayArr} />;
    }
  };

  // 日期列, 周六日的颜色高亮 & Sum行增加提示
  function DateCell({ record, formatMessage, weekDayArr }) {
    const [isIconHover, setIsIconHover] = useState(false);
    const weekDay = getWeekDay(record.date, weekDayArr)?.slice(1, -1);
    const locale = getLocale();

    const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
    const showSumTip = record.date === 'Sum' || record.date === '汇总值';
    const displayDate = moment(record.date).isValid() === true ? moment(record.date).format("YYYY-MM-DD") : record.date;
    return (
      <div style={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>
        <span className='table-text'>{displayDate}</span>
        <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>
        {showSumTip && (
          <div className='port-item'>
            <div onMouseOver={() => { setIsIconHover(true) }} onMouseOut={() => { setIsIconHover(false) }} >
              <Bubble
                dark
                placement="top-start"
                arrowPointAtCenter
                style={{ maxWidth: locale === 'en-US' ? '235px' : '253px' }}
                content={formatMessage({ id: 'overview_sum_tip' })}
              >
                <div><SvgIcon className='port-icon' type={isIconHover ? 'alert-circle-action' : 'alert-circle-m'} /></div>
              </Bubble>
            </div>
          </div>
        )}
      </div>
    );
  }

  const timeZoneColumn = {
    key: timezoneType,
    width: timezoneType === 'country' ? 266 : 196,
    align: 'left',
    fixed: "left",
    header: localText.timezone,
    render: (record) => {
      const imgName = record?.[timezoneType] === "not_found" ? "default" : record?.[timezoneType]?.toLowerCase?.()?.trim?.();
      let ImgDom: JSX.Element;
      try {
        ImgDom = <img style={{ width: '24px', height: '16px', marginRight: '8px', verticalAlign: 'middle' }} src={getCountryFlag(imgName)} alt="flag" />;
      } catch (error) {
        ImgDom = <img style={{ width: '24px', height: '16px', marginRight: '8px', verticalAlign: 'middle' }} src={getCountryFlag('default')} alt="flag" />;
      }
      let text = record?.[`${timezoneType}_name`];
      const isHaveTwoQuote = record?.[`${timezoneType}_name`]?.split("(")?.length > 2;
      const isHaveQuote = text?.includes('UTC') || text?.includes('utc') || text?.includes('Utc');
      if (isHaveQuote) {
        if (isHaveTwoQuote) {
          const strArr = record?.[`${timezoneType}_name`]?.split("(");
          const name = `${strArr?.[0]  }(${  strArr?.[1]}`;
          const utc = strArr?.[2];
          const localText = getDynamicLocalCnEnText(timezoneType, record?.[timezoneType], gameCode, getLocale(), selectorList);
          text = utc ? timeZoneTransformForShow(`${localText}(${utc}`) : name;
        } else {
          const [name = "-", utc] = record?.[`${timezoneType}_name`]?.split("(") || [];
          const localText = getDynamicLocalCnEnText(timezoneType, record?.[timezoneType], gameCode, getLocale(), selectorList);
          text = utc ? timeZoneTransformForShow(`${localText}(${utc}`) : name;
        }
      } else {
        const localText = getDynamicLocalCnEnText(timezoneType, record?.[timezoneType], gameCode, getLocale(), selectorList);
        text = timeZoneTransformForShow(localText);
      }


      return (<div className='dbd-table-cell-nowrap'>
        {
          timezoneType === "country" && imgName && ImgDom
        }
        <span className='dbd-table-cell-ellipsis' title={text}>{record?.[`${timezoneType}_name`] ? text : "-"}</span>
      </div>
      );

    }
  };

  // 获取维度列定义配置
  const getAccumulateColumns = (accumulates = []) => {
    if (accumulates?.length === 1 && accumulates[0] === 'date') return [];
    const columns = [];

    accumulates?.filter(a => a !== 'date').forEach((value) => {
      if (value === timezoneType) return;
      columns.push({
        key: value,
        header: localText[value],
        width: 200,
        render(record) {
          if (!record?.[value]) return "-";
          const text = getDynamicLocalCnEnText(value, record?.[value], gameCode, getLocale(), selectorList);
          return (<span className="dbd-table-cell-ellipsis" title={text}>
            {text}
          </span>);
        }
      });
    });
    accumulates.includes(timezoneType) && columns.unshift(timeZoneColumn);
    return columns;
  };
  const metricColumn = getMetricColumn();

  // 指标列
  const getMetricColumns = (metricsValue = [], metricsObj, explainInfo, showType) => {
    const isShowUpDown = showType !== "value_only";
    return metricsValue.reduce((columns: any, value) => {
      const obj = metricsObj[value];
      const isAot = value === 'aot';
      const metric: any = metricColumn?.[value];
      const width = isShowUpDown ? metric?.width : metric?.onlyValueWidth;
      columns?.push(
        {
          key: obj?.value + showType,
          copyKey: obj?.value,
          header: obj?.text,
          align: "right",
          explain: getExplainVal(explainInfo, obj?.rainbowKey, getLocale()),
          width: width || 200,
          isPercent: obj?.isPercent,
          render(record) {
            const data = record?.metrics?.[obj?.value] || {};
            const isShowChange = showType !== "value_only";

            return (
              <>
                {metricRender(data?.value, data?.[showType.replace("show_", "")], obj, record.type, isShowChange)}
              </>
            )
            ;
          }
        },
      );
      return columns;
    }, []);
  };

  return {
    DATE_COLUMN,
    getAccumulateColumns,
    getMetricColumns
  };
};



